import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const FOLLOWER_RANGES = [
  { key: 'nano', label: 'Nano', range: '1K–10K', min: 1000, max: 10000, color: '#a78bfa' },
  { key: 'micro', label: 'Micro', range: '10K–100K', min: 10000, max: 100000, color: '#6366f1' },
  { key: 'macro', label: 'Macro', range: '100K–1M', min: 100000, max: 1000000, color: '#4f46e5' },
  { key: 'mega', label: 'Mega', range: '1M+', min: 1000000, max: Infinity, color: '#3730a3' },
];

const TIER_COLORS = {
  bronze: '#cd7f32',
  silver: '#9ca3af',
  gold: '#f59e0b',
  platinum: '#6366f1',
};

const CATEGORY_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

const AudienceDemographicsPanel = ({ brandId }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('followers');
  const [followerData, setFollowerData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [tierData, setTierData] = useState([]);
  const [totalInfluencers, setTotalInfluencers] = useState(0);

  const loadDemographics = useCallback(async () => {
    if (!brandId) return;
    try {
      // Get unique influencer IDs from approved transactions
      const { data: txData, error: txErr } = await supabase
        ?.from('transactions')
        ?.select('influencer_id')
        ?.eq('brand_id', brandId)
        ?.eq('status', 'approved');

      if (txErr || !txData?.length) {
        setFollowerData([]);
        setCategoryData([]);
        setTierData([]);
        setTotalInfluencers(0);
        setLoading(false);
        return;
      }

      const uniqueIds = [...new Set(txData.map(t => t.influencer_id))];
      setTotalInfluencers(uniqueIds?.length);

      // Get user profiles with follower ranges and categories
      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, follower_ranges, content_categories, socialcents_score')
        ?.in('id', uniqueIds);

      // Get scores for tier data
      const { data: scores } = await supabase
        ?.from('scores')
        ?.select('user_id, total_score, tier, engagement_score')
        ?.in('user_id', uniqueIds);

      const scoreMap = {};
      scores?.forEach(s => { scoreMap[s.user_id] = s; });

      // Process follower ranges
      const followerCounts = { nano: 0, micro: 0, macro: 0, mega: 0 };
      const categoryCounts = {};
      const tierCounts = { bronze: 0, silver: 0, gold: 0, platinum: 0 };

      profiles?.forEach(p => {
        // Follower ranges from JSONB field
        const fr = p?.follower_ranges || {};
        const totalFollowers = Object.values(fr)?.reduce((sum, v) => sum + (parseInt(v) || 0), 0);
        const range = FOLLOWER_RANGES?.find(r => totalFollowers >= r?.min && totalFollowers < r?.max);
        if (range) followerCounts[range.key] += 1;
        else if (totalFollowers > 0) followerCounts.nano += 1;

        // Content categories
        const cats = p?.content_categories || [];
        cats?.forEach(cat => {
          if (!categoryCounts?.[cat]) categoryCounts[cat] = 0;
          categoryCounts[cat] += 1;
        });

        // Tier from scores
        const score = scoreMap?.[p?.id];
        const tier = score?.tier || 'bronze';
        if (tierCounts?.[tier] !== undefined) tierCounts[tier] += 1;
        else tierCounts.bronze += 1;
      });

      // Format follower data
      setFollowerData(
        FOLLOWER_RANGES?.map(r => ({
          name: r?.label,
          range: r?.range,
          count: followerCounts?.[r?.key],
          color: r?.color,
        }))?.filter(r => r?.count > 0)
      );

      // Format category data (top 8)
      const catArr = Object.entries(categoryCounts)?.map(([name, count], idx) => ({ name, count, color: CATEGORY_COLORS?.[idx % CATEGORY_COLORS?.length] }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 8);
      setCategoryData(catArr);

      // Format tier data
      setTierData(
        Object.entries(tierCounts)?.map(([tier, count]) => ({ name: tier?.charAt(0)?.toUpperCase() + tier?.slice(1), count, color: TIER_COLORS?.[tier] || '#6366f1' }))?.filter(t => t?.count > 0)
      );
    } catch (err) {
      console.error('[AudienceDemographicsPanel] error:', err);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    setLoading(true);
    loadDemographics();
  }, [loadDemographics]);

  useRealtimeSubscription({
    channelName: `brand-demographics-${brandId}`,
    table: 'transactions',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => loadDemographics(),
  });

  const TABS = [
    { key: 'followers', label: 'Follower Ranges', icon: 'Users' },
    { key: 'categories', label: 'Categories', icon: 'Tag' },
    { key: 'tiers', label: 'Engagement Tiers', icon: 'Award' },
  ];

  const activeData = activeTab === 'followers' ? followerData
    : activeTab === 'categories' ? categoryData
    : tierData;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground text-sm">{d?.name}</p>
          {d?.range && <p className="text-xs text-muted-foreground">{d?.range}</p>}
          <p className="text-primary text-sm">{d?.count} influencer{d?.count !== 1 ? 's' : ''}</p>
          <p className="text-xs text-muted-foreground">
            {totalInfluencers > 0 ? ((d?.count / totalInfluencers) * 100)?.toFixed(1) : 0}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-violet-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Audience Demographics</h3>
            <p className="text-xs text-muted-foreground">
              {totalInfluencers} participating influencer{totalInfluencers !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6">
        {TABS?.map(tab => (
          <button
            key={tab?.key}
            onClick={() => setActiveTab(tab?.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              activeTab === tab?.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            <Icon name={tab?.icon} size={12} />
            <span className="hidden sm:inline">{tab?.label}</span>
            <span className="sm:hidden">{tab?.label?.split(' ')?.[0]}</span>
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activeData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="Users" size={40} className="text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground text-sm">No influencer data available yet</p>
          <p className="text-xs text-muted-foreground mt-1">Data appears after transactions are approved</p>
        </div>
      ) : (
        <>
          {/* Bar Chart */}
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
                  {activeData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend / breakdown */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {activeData?.map(item => (
              <div key={item?.name} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item?.color }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{item?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item?.count} ({totalInfluencers > 0 ? ((item?.count / totalInfluencers) * 100)?.toFixed(0) : 0}%)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AudienceDemographicsPanel;
